import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { shortDay, useFormat } from "@/lib/format";
import NoteReader from "@/components/NoteReader";
import { articleName } from "@/services/articles";
import { fetchArticleStocks } from "@/services/articleStocks";
import { fetchDisputedReferences, fetchReceipts, readReceipt, stopReading, validateReference, verifyReceipt,
  type CalCheck, type CalendarCheckState, type Receipt } from "@/services/receiving";
import { LabelLibrary, ReceptionLabels } from "@/components/CartonLabels";

const DOC_TYPES = ["bulletin", "capture", "facture", "etiquettes", "autre"] as const;

// Z Production's "📷 Réception fournisseur": everything that shows what arrived — a photo of each page
// of the delivery note, a screenshot, an invoice, a PDF, the carton labels — is read, and each article
// found goes into article stock. What is not certain is listed "À vérifier", to add by hand.
// manager: his Responsable view, where a reception "à vérifier" is closed (« ✓ Vérifié et corrigé ») and
// a disputed supplier reference settled (« Valider ») — Ludovic's answers of 03.11.2026, lines 15-16.
export default function ArticleReception({ warehouseId, manager = false }: { warehouseId: number | null; manager?: boolean }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [docType, setDocType] = useState("");
  const [note, setNote] = useState("");
  const [supplier, setSupplier] = useState("");
  const [library, setLibrary] = useState(false);
  const list = useQuery({
    queryKey: ["article-receipts", warehouseId], queryFn: () => fetchReceipts(warehouseId, "articles"), enabled: !!warehouseId,
    refetchInterval: (q) => (q.state.data?.receipts.some((r) => r.status === "reading") ? 4000 : false),
  });
  const refresh = () => ["article-receipts", "article-stocks", "stock-movements"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
  // The reading runs on the worker: when a reception stops being read, the stock it changed is
  // fetched again, so the article list below never shows yesterday's number.
  const reading = list.data?.receipts.filter((r) => r.status === "reading").length ?? 0;
  const wasReading = useRef(0);
  useEffect(() => {
    if (reading < wasReading.current) ["article-stocks", "stock-movements"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
    wasReading.current = reading;
  }, [reading, qc]);

  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <h2 className="font-medium text-slate-900">{t("artrec.title")}</h2>
        {/* His "🗂️ Base d'étiquettes": a label for any reference, at any time, without a reception. */}
        <button onClick={() => setLibrary(true)} className="rounded-lg px-3 py-1 text-sm ring-1 ring-slate-300">🗂️ {t("carton.libTitle")}</button>
      </div>
      {library && <LabelLibrary onClose={() => setLibrary(false)} />}
      <p className="px-5 pt-2 text-xs text-slate-500">{t("artrec.hint")} {t("artrec.hintPaste")}</p>
      {!warehouseId ? <p className="px-5 py-4 text-sm text-slate-500">{t("stock.pickWarehouse")}</p> : (
        <>
          <div className="pt-2">
            <NoteReader warehouseId={warehouseId} purpose="articles"
              extra={{ declared_type: docType || null, note: note.trim() || null, supplier_name: supplier.trim() || null }}
              onStarted={() => { setDocType(""); setNote(""); setSupplier(""); refresh(); }}>
              <div className="mt-2 flex flex-wrap gap-2">
                <input value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={80} placeholder={t("artrec.supplier")}
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                  <option value="">{t("artrec.typeAny")}</option>
                  {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`artrec.type.${d}`)}</option>)}
                </select>
                <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={t("artrec.note")}
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
              </div>
            </NoteReader>
          </div>
          <h3 className="border-t border-slate-100 px-5 pb-1 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("artrec.latest")}</h3>
          {list.data?.calendar_check?.warehouse_id === warehouseId && <CalendarCheckHead cal={list.data.calendar_check} manager={manager} />}
          {list.data && list.data.receipts.length === 0 && <p className="px-5 pb-4 text-sm text-slate-400">{t("artrec.none")}</p>}
          <ul className="divide-y divide-slate-100">
            {/* His last eight, and every reception still "à vérifier" however old. */}
            {list.data?.receipts.filter((r, i) => i < 8 || r.stocked?.to_check).map((r) => (
              <Row key={r.id} r={r} manager={manager} warehouseId={warehouseId} onChanged={refresh} />
            ))}
          </ul>
          {manager && <DisputedReferences warehouseId={warehouseId} />}
        </>
      )}
    </section>
  );
}

function Row({ r, manager, warehouseId, onChanged }: { r: Receipt; manager: boolean; warehouseId: number; onChanged: () => void }) {
  const { t, locale } = useT();
  const { dateTime } = useFormat();
  const again = useMutation({ mutationFn: () => readReceipt(r.id), onSuccess: onChanged });
  const stop = useMutation({ mutationFn: () => stopReading(r.id), onSuccess: onChanged });
  const [labels, setLabels] = useState(false);
  const verify = r.stocked?.to_verify ?? [];
  const items = r.stocked?.items ?? [];
  const reserved = r.stocked?.client_reserved ?? [];
  // His A20 status: "a_verifier" when a line is left to check or when nothing could be read.
  const toCheck = r.stocked?.to_check ?? verify.length > 0;
  // His REC_STATUS.
  const [label, tone] = r.status === "reading" ? [t("artrec.st.running"), "bg-sky-100 text-sky-800"]
    : r.status === "failed" ? [t("note.failed"), "bg-red-100 text-red-800"]
    : r.status === "stopped" ? [`⏹ ${t("note.stopped")}`, "bg-slate-200 text-slate-700"]
    : r.status === "read" ? (toCheck ? [t("artrec.st.verify"), "bg-amber-100 text-amber-800"] : [t("artrec.st.done"), "bg-emerald-100 text-emerald-800"])
    : r.status === "verified" ? [t("artrec.st.verified"), "bg-emerald-100 text-emerald-800"]
    : [t("artrec.st.waiting"), "bg-slate-200 text-slate-700"];
  return (
    <li className="px-5 py-2.5 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}>{label}</span>
        {r.status === "reading" && (
          <button onClick={() => stop.mutate()} disabled={stop.isPending} className="rounded px-2 py-0.5 text-xs ring-1 ring-slate-300">{t("note.stop")}</button>
        )}
        <span className="text-slate-700">{[r.supplier_name, r.created_by, r.declared_type && t(`artrec.type.${r.declared_type}`)].filter(Boolean).join(" · ")}</span>
        <span className="ml-auto text-xs text-slate-400">{dateTime(r.read_at ?? r.reading_started_at)}</span>
      </div>
      {r.note && <p className="text-xs text-slate-600">{t("artrec.noteShown")} {r.note}</p>}
      {/* His reception photos: thumbnails, a click opens the full size. */}
      {!!r.media?.length && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {r.media.map((m, i) => m.url && (
            <a key={m.id} href={m.url} target="_blank" rel="noreferrer" title={m.original_filename ?? undefined}>
              {m.kind === "image"
                ? <img src={m.thumb_url ?? m.url} alt={t("artrec.photoN", { n: i + 1 })} className="h-14 w-14 rounded border border-slate-200 object-cover" />
                : <span className="flex h-14 w-14 items-center justify-center rounded border border-slate-200 bg-slate-50 text-xl">📄</span>}
            </a>
          ))}
        </div>
      )}
      {items.length > 0 && (
        <p className="text-xs text-emerald-800">
          {t("artrec.updated")} {items.map((i) => `${articleName(i.reference, locale) ?? i.reference} +${i.qty}${i.total != null ? ` (${t("artrec.total")} ${i.total})` : ""}`).join(", ")}
        </p>
      )}
      {/* His A20 "Réservé client (non entré en stock) : Fer à repasser (1KE) ×10 pour Etang". */}
      {reserved.length > 0 && (
        <p className="text-xs text-slate-700">
          {t("artrec.clientReserved")} {reserved.map((c) => `${(c.reference && articleName(c.reference, locale)) ?? c.desc ?? c.reference ?? "?"}${c.reference ? ` (${c.reference})` : ""} ×${c.qty ?? "?"} ${t("artrec.forClient")} ${c.client}`).join(", ")}
        </p>
      )}
      {r.stocked?.summary && <p className="text-xs text-slate-500">{t("artrec.summary")} {r.stocked.summary}</p>}
      {/* His "🏷️ Imprimer les étiquettes cartons", on a reception read with articles. */}
      {r.status === "read" && items.length > 0 && (
        <button onClick={() => setLabels(true)} className="mt-1 rounded-lg px-2 py-0.5 text-xs ring-1 ring-slate-300">🏷️ {t("carton.printCartons")}</button>
      )}
      {labels && <ReceptionLabels items={items} supplier={r.supplier_name} date={shortDay(r.read_at ?? r.reading_started_at ?? new Date().toISOString())} onClose={() => setLabels(false)} />}
      {verify.length > 0 && (
        <p className="text-xs text-amber-800">⚠ {t("artrec.toVerify")} {verify.map((v) => `${v.no ?? "?"} ${v.desc ?? ""}${v.qty != null ? ` ×${v.qty}` : ""} (${v.why})`).join(" ; ")}</p>
      )}
      {r.stocked?.verified && <Verified v={r.stocked.verified} />}
      {r.stocked?.cal_check && <CalCheckLine cc={r.stocked.cal_check} />}
      {manager && r.status === "read" && toCheck && <CheckBlock r={r} warehouseId={warehouseId} onDone={onChanged} />}
      {r.status === "stopped" && (
        <button onClick={() => again.mutate()} disabled={again.isPending} className="text-xs underline">{t("note.retry")}</button>
      )}
      {r.status === "failed" && (
        // Why, in the reader's language — never the model's own English (the same words as Receiving).
        <p className="text-xs text-red-700">{t(`note.err.${r.read_error_kind ?? "failed"}`)}{" "}
          <button onClick={() => again.mutate()} disabled={again.isPending} className="underline">{t("note.retry")}</button></p>
      )}
    </li>
  );
}

// His « Vérifiée et corrigée »: who, when, the note, and each stock correction with its new total.
function Verified({ v }: { v: NonNullable<NonNullable<Receipt["stocked"]>["verified"]> }) {
  const { t, locale } = useT();
  const { dateTime } = useFormat();
  return (
    <div className="mt-1 text-xs text-emerald-800">
      <p>✓ {t("artrec.verifiedBy", { at: dateTime(v.at), by: v.by ?? "?" })} {v.note}</p>
      {v.corrections.length > 0 && (
        <p>{t("artrec.corrections")} {v.corrections.map((c) => `${articleName(c.code, locale) ?? c.name ?? c.code} (${c.code}) ${c.delta > 0 ? "+" : ""}${c.delta} → ${c.new_qty}`).join(", ")}</p>
      )}
    </div>
  );
}

// His task A28 at the top of the list (index.html:6392-6405): for everyone, the Penthalaz orders the
// Delivery calendar says arrived with no photo yet; for the manager, when the check last ran. The feed
// gives no warehouse per order line, so a line of a Penthalaz order delivered elsewhere is still counted
// — the manager is told.
function CalendarCheckHead({ cal, manager }: { cal: CalendarCheckState; manager: boolean }) {
  const { t, locale } = useT();
  const { zurichDateTime } = useFormat();
  return (
    <div className="space-y-1 px-5 pb-1">
      {cal.missing.length > 0 && (
        <p className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 ring-1 ring-amber-200">
          📅 {t("artrec.cal.missing")} {cal.missing.map((m) => `${shortDay(m.arrival)}${m.supplier ? ` — ${m.supplier}` : ""}${m.items.length
            ? ` (${m.items.map((i) => `${articleName(i.code, locale) ?? i.code} ×${i.qty}`).join(", ")})` : ""}`).join(" ; ")}. {t("artrec.cal.missingAsk")}
        </p>
      )}
      {manager && cal.last_run_at && (
        <div className="text-xs text-slate-500">
          <p>📅 {t("artrec.cal.last", { at: zurichDateTime(cal.last_run_at) })}{cal.last_status && cal.last_status !== "ok" ? ` (${t("artrec.cal.error")})` : ""}</p>
          <p>{t("artrec.cal.otherWarehouses")}</p>
          {cal.catalogue_imported === false && <p>{t("artrec.cal.noCatalogue")}</p>}
          {cal.messages === false && <p>{t("artrec.cal.held")}</p>}
        </div>
      )}
    </div>
  );
}

// His A28 line on one reception: the verdict, the order(s), what differs, when it was checked.
function CalCheckLine({ cc }: { cc: CalCheck }) {
  const { t, locale } = useT();
  const { zurichDateTime } = useFormat();
  const name = (i: { code: string; name: string | null }) => articleName(i.code, locale) ?? i.name ?? i.code;
  const items = cc.items ?? [];
  const note = cc.status === "ok" ? items.map((i) => `${name(i)} ×${i.ordered}`).join(", ")
    : cc.status === "ecart" ? items.map((i) => i.ordered != null
      ? t("artrec.cal.diff", { name: name(i), code: i.code, ordered: i.ordered, photo: i.photo })
      : t("artrec.cal.extra", { name: name(i), code: i.code, photo: i.photo })).join(" ; ")
    : cc.status === "non_livree" ? t("artrec.cal.notDeliveredNote", { statuses: (cc.order_statuses ?? []).join("/") })
    : t("artrec.cal.absentNote", { since: shortDay(cc.since) });
  const tone = cc.status === "ok" ? "text-emerald-800" : cc.status === "ecart" ? "text-amber-800" : "text-slate-600";
  return (
    <p className={`text-xs ${tone}`}>
      {t(`artrec.cal.st.${cc.status}`)}
      {cc.order_ids.length > 0 && ` (${t("artrec.cal.orders", { ids: cc.order_ids.map((x) => `#${x}`).join(", ") })})`}
      {note && ` — ${note}`}
      {cc.at && ` (${zurichDateTime(cc.at)})`}
    </p>
  );
}

// His check block on a reception "à vérifier": optional stock corrections (article + quantity, plus or
// minus), a required note, and « ✓ Vérifié et corrigé ». Each correction goes through the stock ledger.
function CheckBlock({ r, warehouseId, onDone }: { r: Receipt; warehouseId: number; onDone: () => void }) {
  const { t, locale } = useT();
  const stocks = useQuery({ queryKey: ["article-stocks", warehouseId], queryFn: () => fetchArticleStocks(warehouseId) });
  const articles = (stocks.data?.groups ?? []).flatMap((g) => g.articles);
  const held = new Map(articles.map((a) => [a.reference, a.qty]));
  const [rows, setRows] = useState<{ code: string; delta: string }[]>([]);
  const [note, setNote] = useState("");
  const corrections = rows.filter((x) => x.code && x.delta.trim()).map((x) => ({ code: x.code, delta: Number(x.delta) }));
  const save = useMutation({ mutationFn: () => verifyReceipt(r.id, note.trim(), corrections), onSuccess: onDone });
  const set = (i: number, patch: Partial<{ code: string; delta: string }>) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="mt-2 rounded-lg bg-amber-50 p-2.5 ring-1 ring-amber-200">
      <p className="text-xs font-medium text-amber-900">{t("artrec.check.title")}</p>
      <p className="text-xs text-amber-800">{t("artrec.check.hint")}</p>
      {rows.map((x, i) => {
        const n = Number(x.delta);
        const now = held.get(x.code) ?? 0;
        return (
          <div key={i} className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <select value={x.code} onChange={(e) => set(i, { code: e.target.value })} className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-xs">
              <option value="">{t("artrec.check.pick")}</option>
              {articles.map((a) => <option key={a.reference} value={a.reference}>{a.reference} — {articleName(a.reference, locale) ?? a.reference}</option>)}
            </select>
            <input value={x.delta} onChange={(e) => set(i, { delta: e.target.value })} inputMode="numeric" placeholder={t("artrec.check.qty")}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs" />
            {x.code && Number.isInteger(n) && n !== 0 && <span className="text-xs text-slate-600">{now} → {Math.max(now + n, 0)}</span>}
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={t("artrec.check.remove")} className="px-1 text-xs text-slate-500">✕</button>
          </div>
        );
      })}
      <button onClick={() => setRows([...rows, { code: "", delta: "" }])} className="mt-1.5 rounded px-2 py-0.5 text-xs ring-1 ring-amber-300">+ {t("artrec.check.add")}</button>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2} placeholder={t("artrec.check.note")}
        className="mt-1.5 block w-full rounded border border-slate-300 px-2 py-1 text-xs" />
      {save.isError && <p className="mt-1 text-xs text-red-700">{(save.error as Error).message}</p>}
      <button onClick={() => save.mutate()} disabled={!note.trim() || save.isPending}
        className="mt-1.5 rounded-lg bg-emerald-700 px-3 py-1 text-xs font-medium text-white disabled:opacity-40">✓ {t("artrec.check.button")}</button>
    </div>
  );
}

// His « Références fournisseur contestées »: a reference the reading treats as absent until the right
// article is chosen and « Valider » is clicked.
function DisputedReferences({ warehouseId }: { warehouseId: number }) {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const refs = useQuery({ queryKey: ["disputed-references"], queryFn: fetchDisputedReferences });
  const stocks = useQuery({ queryKey: ["article-stocks", warehouseId], queryFn: () => fetchArticleStocks(warehouseId) });
  const articles = (stocks.data?.groups ?? []).flatMap((g) => g.articles.map((a) => a.reference));
  const [picked, setPicked] = useState<Record<number, string>>({});
  const validate = useMutation({
    mutationFn: (v: { id: number; code: string }) => validateReference(v.id, v.code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["disputed-references"] }),
  });
  const list = refs.data?.references ?? [];
  if (!list.length) return null;
  return (
    <div className="border-t border-slate-100 px-5 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800">{t("artrec.disputed.title")}</h3>
      <p className="text-xs text-slate-500">{t("artrec.disputed.hint")}</p>
      <ul className="mt-1 space-y-1.5">
        {list.map((ref) => {
          const code = picked[ref.id] ?? ref.article_reference ?? "";
          return (
            <li key={ref.id} className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-mono text-slate-800">{ref.supplier_reference}</span>
              <span className="text-slate-600">{[ref.supplier, ref.supplier_name].filter(Boolean).join(" · ")}</span>
              {ref.article_reference && <span className="text-slate-500">{t("artrec.disputed.now", { code: ref.article_reference })}</span>}
              <select value={code} onChange={(e) => setPicked({ ...picked, [ref.id]: e.target.value })} className="ml-auto rounded border border-slate-300 px-2 py-1 text-xs">
                <option value="">{t("artrec.check.pick")}</option>
                {articles.map((a) => <option key={a} value={a}>{a} — {articleName(a, locale) ?? a}</option>)}
              </select>
              <button onClick={() => validate.mutate({ id: ref.id, code })} disabled={!code || validate.isPending}
                className="rounded-lg bg-emerald-700 px-2.5 py-1 font-medium text-white disabled:opacity-40">{t("artrec.disputed.validate")}</button>
            </li>
          );
        })}
      </ul>
      {validate.isError && <p className="mt-1 text-xs text-red-700">{(validate.error as Error).message}</p>}
    </div>
  );
}
